import { useState } from 'react';
import { useAuth } from '@/contexts/auth-context';
import QUERY_KEYS from '@/constants/query-keys';
import api from '@/lib/api';

interface ExcelRequest {
  [key: string]: string[];
}

interface ExcelResponse {
  [key: string]: any;
}

interface UseExcelReturn {
  data: ExcelResponse | null;
  isLoading: boolean;
  error: string | null;
  getUUIDByCode: (requestData: ExcelRequest) => Promise<ExcelResponse | null>;
}

/**
 * Hook for getting UUID by code from Excel API
 *
 * This hook provides a function to fetch UUIDs based on various code parameters.
 */
export const useExcel = (): UseExcelReturn => {
  const [data, setData] = useState<ExcelResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { entity } = useAuth();

  const getUUIDByCode = async (requestData: ExcelRequest): Promise<ExcelResponse | null> => {
    if (!entity?.slug) {
      setError('Entity slug is required');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post<ExcelResponse>(`/entities/${entity.slug}/erp/${QUERY_KEYS.EXCEL}/`, requestData);
      setData(response.data);
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Có lỗi xảy ra khi lấy UUID';
      setError(errorMessage);
      setData(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    data,
    isLoading,
    error,
    getUUIDByCode
  };
};
